type ErrorSource = "window.error" | "unhandledrejection" | "react";

export type FrontendErrorReport = {
  source: ErrorSource;
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
};

declare global {
  interface Window {
    __MEDESSA_ERROR_LOGS__?: FrontendErrorReport[];
  }
}

const MAX_BUFFERED_REPORTS = 25;

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Unknown frontend error";
};

const getErrorStack = (error: unknown) => {
  if (error instanceof Error) return error.stack;
  return undefined;
};

export const reportFrontendError = (
  source: ErrorSource,
  error: unknown,
  componentStack?: string,
) => {
  const report: FrontendErrorReport = {
    source,
    message: getErrorMessage(error),
    stack: getErrorStack(error),
    componentStack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  };

  window.__MEDESSA_ERROR_LOGS__ = [report, ...(window.__MEDESSA_ERROR_LOGS__ ?? [])].slice(
    0,
    MAX_BUFFERED_REPORTS,
  );

  console.groupCollapsed(`[frontend-error] ${report.source}: ${report.message}`);
  console.error(report);
  console.groupEnd();

  const endpoint = import.meta.env.VITE_ERROR_REPORTING_ENDPOINT;
  if (endpoint) {
    navigator.sendBeacon?.(endpoint, JSON.stringify(report));
  }

  return report;
};

export const initFrontendErrorReporting = () => {
  window.addEventListener("error", (event) => {
    reportFrontendError("window.error", event.error ?? event.message);
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportFrontendError("unhandledrejection", event.reason);
  });
};